import * as React from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { authedFetch } from '@/providers/loginWithEmail/dataProvider';

type ReopenQuotingDialogProps = {
    open: boolean;
    projectId: number;
    onClose: () => void;
};

function parseReopenError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;

    const errors = obj.errors;
    if (errors && typeof errors === 'object') {
        const errorsObj = errors as Record<string, unknown>;
        const root = errorsObj.root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
        for (const value of Object.values(errorsObj)) {
            if (typeof value === 'string') return value;
        }
    }

    const detail = obj.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const first = detail[0];
        if (first && typeof first === 'object' && first !== null && 'msg' in first) {
            return String((first as { msg: unknown }).msg);
        }
    }
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

async function reopenProjectToQuoting(projectId: number): Promise<{ id: number }> {
    const res = await authedFetch(`/projects/${projectId}/reopen-quoting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });
    const raw = await res.text();
    let data: unknown = {};
    if (raw) {
        try {
            data = JSON.parse(raw);
        } catch {
            data = { detail: raw.slice(0, 400) };
        }
    }
    if (!res.ok) {
        const parsed = parseReopenError(data, '');
        if (parsed) throw new Error(parsed);
        const snippet =
            typeof data === 'object' && data !== null && Object.keys(data as object).length
                ? JSON.stringify(data).slice(0, 400)
                : raw.slice(0, 400);
        throw new Error(
            snippet
                ? `Failed to move project back to Quotes (HTTP ${res.status}): ${snippet}`
                : `Failed to move project back to Quotes (HTTP ${res.status})`,
        );
    }
    return data as { id: number };
}

export const ReopenQuotingDialog: React.FC<ReopenQuotingDialogProps> = ({
    open,
    projectId,
    onClose,
}) => {
    const notify = useNotify();
    const navigate = useNavigate();
    const mutation = useMutation({
        mutationFn: () => reopenProjectToQuoting(projectId),
        onSuccess: (data) => {
            notify('Project moved back to Quotes.', { type: 'success' });
            onClose();
            navigate(`/quotes/folders/${data.id}`);
        },
        onError: (error: Error) => {
            notify(error.message, { type: 'error' });
        },
    });

    return (
        <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Move back to Quotes</DialogTitle>
            <DialogContent>
                <Typography>
                    The client went cold after Approve. This moves the job back to
                    Quotes (folder under 2. Quotes, quote status pending again).
                    Project number and related people stay the same. Only admins can
                    do this.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={() => mutation.mutate()}
                    disabled={mutation.isPending}
                >
                    {mutation.isPending ? <CircularProgress size={20} /> : 'Move to Quotes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
